<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        v-bind:title="
          isEdit ? 'Sửa phiếu đề nghị mua hàng' : 'Tạo phiếu đề nghị mua hàng'
        "
      >
        <template v-slot:preview>
          <b-form @submit.prevent="onSubmit">
            <b-row class="mb-3">
              <b-col cols="3">
                <b-form-group>
                  <template>
                    <label>Doanh nghiệp:</label>
                    <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    size="sm"
                    v-model="form.companyId"
                    :options="listCompany"
                    value-field="id"
                    text-field="name"
                    required
                    @change="onBudgetParamChange"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled
                        >-- Doanh nghiệp --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <template #label>
                    Phòng ban <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="departmentModel"
                    :suggestions="departmentSuggestions"
                    :suggestionName="'name'"
                    :placeholder="'phòng ban'"
                    :limit="50"
                    :disabled="isLoadingFilters"
                    @select="onSelected($event, 'Department')"
                    @change="onInputed($event, 'Department')"
                  >
                    <template #custom="{ suggestion }">
                      {{ suggestion.item.name }}
                    </template>
                  </Autosuggest>
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <template #label>
                    Khoản mục phí <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    v-model="form.costItemId"
                    :options="costItemOptions"
                    required
                    @change="onBudgetParamChange"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <template #label>
                    Cửa hàng <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="storeModel"
                    :suggestions="storeSuggestions"
                    :suggestionName="'name'"
                    :placeholder="'cửa hàng'"
                    :limit="50"
                    :disabled="isLoadingFilters"
                    @select="onSelected($event, 'Store')"
                    @change="onInputed($event, 'Store')"
                  >
                    <template #custom="{ suggestion }">
                      {{ suggestion.item.name }}
                    </template>
                  </Autosuggest>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="2">
                <b-form-group>
                  <template #label>
                    Tháng <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    v-model.number="form.month"
                    :options="monthOptions"
                    required
                    @change="onBudgetParamChange"
                    size="sm"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <b-form-group>
                  <template #label>
                    Năm <span class="text-danger">*</span>
                  </template>
                  <date-picker
                    ref="yearDatePicker"
                    class="form-control form-control-sm pr-year-picker"
                    :config="yearPickerConfig"
                    v-model="yearModel"
                    placeholder="Chọn năm"
                    @input="onYearChange"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="3">
                <b-form-group>
                  <template #label>
                    Số tiền đề nghị (VNĐ) <span class="text-danger">*</span>
                  </template>
                  <BInputNumber
                    v-model.number="form.requestedAmount"
                    placeholder="0"
                    size="sm"
                    required
                    @input="onBudgetParamChange"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="2">
                <b-form-group label="Số tháng SD NS">
                  <BInputNumber
                    v-model.number="form.budgetUsagePeriod"
                    placeholder="1"
                    size="sm"
                    @valueChange="onBudgetUsagePeriodChange"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="8">
                <b-form-group label="Diễn giải">
                  <b-form-textarea
                    v-model="form.description"
                    rows="3"
                    placeholder="Nhập diễn giải"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="mb-4">
              <b-col cols="8">
                <b-card class="mb-0">
                  <h6 class="mb-3">Quy trình duyệt</h6>
                  <div v-if="approvalSteps.length">
                    <b-row
                      v-for="(step, index) in approvalSteps"
                      :key="`approval-step-${step.step}-${index}`"
                      class="mb-2"
                    >
                      <b-col cols="4" class="d-flex align-items-center">
                        <strong>Bước {{ step.step }}</strong>
                      </b-col>
                      <b-col cols="8">
                        <b-form-select
                          v-model.number="step.approverBy"
                          :options="step.ownerOptions"
                        />
                      </b-col>
                    </b-row>
                  </div>
                  <div v-else class="text-muted">
                    Chưa có cấu hình duyệt cho loại phiếu này.
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- Budget check info -->
            <b-row v-if="budgetCheck" class="mb-4">
              <b-col cols="8">
                <b-card
                  :border-variant="
                    budgetCheck.isOverBudget ? 'danger' : 'success'
                  "
                  class="mb-0"
                >
                  <h6>
                    Thông tin ngân sách —
                    <b-badge :variant="budgetLevelVariant">{{
                      budgetLevelLabel
                    }}</b-badge>
                  </h6>
                  <b-row>
                    <b-col cols="4">
                      <small class="text-muted">Phân bổ:</small>
                      <div>
                        <strong>{{
                          convertPrice(budgetCheck.allocatedAmount)
                        }}</strong>
                      </div>
                    </b-col>
                    <b-col cols="4">
                      <small class="text-muted">Đã dùng:</small>
                      <div>
                        <strong>{{
                          convertPrice(budgetCheck.usedAmount)
                        }}</strong>
                      </div>
                    </b-col>
                    <b-col cols="4">
                      <small class="text-muted">Còn lại sau PR:</small>
                      <div
                        :class="
                          budgetCheck.isOverBudget
                            ? 'text-danger'
                            : 'text-success'
                        "
                      >
                        <strong>{{
                          convertPrice(budgetCheck.remainingAfterPR)
                        }}</strong>
                      </div>
                    </b-col>
                  </b-row>
                  <b-alert
                    v-if="
                      budgetCheck.isOverBudget &&
                      budgetCheck.budgetLevel !== 'NOT_FOUND'
                    "
                    show
                    variant="warning"
                    class="mt-2 mb-0"
                  >
                    <i class="fas fa-exclamation-triangle"></i> Vượt ngân sách!
                    Phiếu vẫn có thể gửi duyệt.
                  </b-alert>
                  <b-alert
                    v-else-if="budgetCheck.budgetLevel === 'NOT_FOUND'"
                    show
                    variant="warning"
                    class="mt-2 mb-0"
                  >
                    <i class="fas fa-exclamation-triangle"></i> Chưa khai báo
                    ngân sách cho cấu hình ngân sách này.
                  </b-alert>
                </b-card>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <b-button type="submit" variant="primary" :disabled="isSaving">
                  <i class="fas fa-save"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
                </b-button>
                <b-button variant="secondary" class="ml-2" @click="goBack">
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  xoa_dau,
} from '@/utils/common';
import { debounce } from 'lodash';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import timeUtils from '@/utils/date';
import moment from 'moment';
import { handleError } from '@/utils/common';

export default {
  components: { KTCodePreview, Autosuggest, BInputNumber },
  data() {
    const currentDate = new Date();
    return {
      isEdit: false,
      isSaving: false,
      budgetCheck: null,
      approvalSteps: [],
      approvalConfigDetails: [],
      approvalType: 9,
      listCompany: [],
      isLoadingFilters: false,
      suggestLimit: 50,
      departmentModel: '',
      storeModel: '',
      yearModel: moment(String(currentDate.getFullYear()), 'YYYY'),
      yearPickerConfig: {
        ...timeUtils.DP_CONFIG.date,
        format: 'YYYY',
        viewMode: 'years',
        ignoreReadonly: true,
        allowInputToggle: true,
        useCurrent: false,
        showClear: true,
        showClose: true,
      },
      departmentSource: [],
      departmentSuggestions: [],
      storeSuggestions: [],
      form: {
        id: null,
        companyId: null,
        departmentId: null,
        costItemId: null,
        storeId: null,
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        requestedAmount: 0,
        budgetUsagePeriod: 1,
        description: '',
      },
      costItemOptions: [{ value: null, text: '-- Chọn khoản mục --' }],
      monthOptions: Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        text: `Tháng ${i + 1}`,
      })),
    };
  },
  computed: {
    budgetLevelLabel() {
      if (!this.budgetCheck) return '';
      const map = {
        STORE: 'Cửa hàng',
        DEPARTMENT: 'Phòng ban',
        NOT_FOUND: 'Chưa khai báo ngân sách',
      };
      return (
        map[this.budgetCheck.budgetLevel] || this.budgetCheck.budgetLevel || ''
      );
    },
    budgetLevelVariant() {
      if (!this.budgetCheck) return 'secondary';
      const map = {
        STORE: 'info',
        DEPARTMENT: 'primary',
        NOT_FOUND: 'warning',
      };
      return map[this.budgetCheck.budgetLevel] || 'secondary';
    },
  },
  created() {
    this.fetchCompany();
    this.fetchCostItems();
    this.fetchDepartments();
    this.fetchStores();
    this.fetchApprovalConfig();
    const id = this.$route.query.id;
    if (id) {
      this.isEdit = true;
      this.fetchDetail(id);
    }
    this.debouncedCheckBudget = debounce(this.checkBudget, 500);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Phiếu đề nghị mua hàng', route: '/budget/purchase-requests' },
      { title: this.isEdit ? 'Sửa phiếu' : 'Tạo phiếu' },
    ]);
    // Chỉ chọn năm từ lịch — không cho gõ tay
    this.$nextTick(() => {
      const root = this.$refs.yearDatePicker && this.$refs.yearDatePicker.$el;
      const input =
        root && root.tagName === 'INPUT'
          ? root
          : root && root.querySelector && root.querySelector('input');
      if (input) {
        input.setAttribute('readonly', 'readonly');
        input.setAttribute('autocomplete', 'off');
      }
    });
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    /** Giống BudgetLineDeclareForm: filter theo @change + xoa_dau */
    filterSuggestionsByName(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) {
        return list.slice(0, limit);
      }
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          const nameNoneSign = xoa_dau(name);
          return (
            nameNoneSign.toLowerCase().indexOf(tLower) > -1 ||
            name.toLowerCase().indexOf(tLower) > -1
          );
        })
        .slice(0, limit);
    },
    syncDepartmentSuggestionsFromModel() {
      this.departmentSuggestions = this.filterSuggestionsByName(
        this.departmentSource,
        this.departmentModel,
        this.suggestLimit,
      );
    },
    onBudgetUsagePeriodChange(val) {
      const n = Number(val);
      const clamped = Math.max(1, Math.min(12, Number.isFinite(n) ? n : 1));
      this.form.budgetUsagePeriod = clamped;
      this.onBudgetParamChange();
    },
    onYearChange(value) {
      let year = null;
      if (value == null) return;
      if (moment.isMoment(value)) year = value.year();
      else if (value instanceof Date) year = value.getFullYear();
      else if (typeof value === 'number') year = value;
      else if (typeof value === 'string') {
        const match = value.match(/\d{4}/);
        year = match ? Number(match[0]) : null;
      }

      if (year && year >= 2020 && year <= 2100) {
        this.form.year = year;
        this.yearModel = moment(String(year), 'YYYY');
        this.onBudgetParamChange();
      }
    },
    fetchCompany() {
      ApiService.get('/company').then((response) => {
        this.listCompany = response.data.data;
      });
    },
    fetchDepartments() {
      this.isLoadingFilters = true;
      return ApiService.query('departments/all')
        .then(({ data }) => {
          const rows = data.data || [];
          this.departmentSource = rows.map((d) => ({
            id: Number(d.id),
            name: d.name,
          }));
          this.syncDepartmentSuggestionsFromModel();
        })
        .catch(() => {
          this.departmentSource = [];
          this.departmentSuggestions = [];
        })
        .finally(() => {
          this.isLoadingFilters = false;
          this.syncDepartmentModel();
        });
    },
    fetchCostItems() {
      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const items = data.data.map((i) => ({
          value: i.id,
          text: i.name,
        }));
        this.costItemOptions = [
          { value: null, text: '-- Chọn khoản mục --' },
          ...items,
        ];
      });
    },
    fetchStores() {
      this.isLoadingFilters = true;
      return ApiService.query('stores/getStores')
        .then(({ data }) => {
          const rows = data.data || [];
          this.storeSuggestions = rows.map((s) => ({
            id: Number(s.id),
            name: s.name,
            shortName: s.shortName,
          }));
        })
        .catch(() => {
          this.storeSuggestions = [];
        })
        .finally(() => {
          this.isLoadingFilters = false;
          this.syncStoreModel();
        });
    },
    syncDepartmentModel() {
      if (!this.form.departmentId) {
        this.departmentModel = '';
        return;
      }
      const found = this.departmentSource.find(
        (d) => Number(d.id) === Number(this.form.departmentId),
      );
      if (found) this.departmentModel = found.name;
    },
    syncStoreModel() {
      if (!this.form.storeId) {
        this.storeModel = '';
        return;
      }
      const found = this.storeSuggestions.find(
        (s) => Number(s.id) === Number(this.form.storeId),
      );
      if (found) {
        this.storeModel = found.shortName
          ? `${found.name} (${found.shortName})`
          : found.name;
      }
    },
    onSelected(option, type) {
      const item = option && option.item ? option.item : null;
      if (type === 'Department') {
        this.form.departmentId = item ? Number(item.id) : null;
        this.departmentModel = item ? item.name : '';
        this.departmentSuggestions = this.departmentSource.slice(
          0,
          this.suggestLimit,
        );
      }
      if (type === 'Store') {
        this.form.storeId = item ? Number(item.id) : null;
        this.storeModel = item
          ? item.shortName
            ? `${item.name} (${item.shortName})`
            : item.name
          : '';
      }
      this.onBudgetParamChange();
    },
    onInputed(text, type) {
      const value = (text || '').toString();
      if (type === 'Department') {
        this.departmentModel = value;
        if (!value.trim()) this.form.departmentId = null;
        this.syncDepartmentSuggestionsFromModel();
      }
      if (type === 'Store') {
        this.storeModel = value;
        if (!value.trim()) this.form.storeId = null;
      }
      this.onBudgetParamChange();
    },
    mapApprovalSteps(details, selectedApprovers = []) {
      const selectedByStep = selectedApprovers.reduce((acc, item) => {
        acc[item.step] = item.approverBy;
        return acc;
      }, {});
      this.approvalSteps = (details || [])
        .sort((a, b) => a.step - b.step)
        .map((detail) => {
          const uniqueOwners = [];
          const ownerMap = {};
          (detail.owners || []).forEach((owner) => {
            if (!owner || !owner.id || ownerMap[owner.id]) return;
            ownerMap[owner.id] = true;
            uniqueOwners.push(owner);
          });
          const ownerOptions = [
            { value: null, text: `-- Chọn người duyệt bước ${detail.step} --` },
            ...uniqueOwners.map((owner) => ({
              value: Number(owner.id),
              text: owner.name ? `${owner.name} (${owner.id})` : `${owner.id}`,
            })),
          ];
          return {
            step: detail.step,
            approverBy: selectedByStep[detail.step] || null,
            ownerOptions,
          };
        });
    },
    fetchApprovalConfig(selectedApprovers = []) {
      ApiService.get(`v2/approval-configs/by-type/${this.approvalType}`)
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.approvalConfigDetails = data.data.details || [];
            this.mapApprovalSteps(
              this.approvalConfigDetails,
              selectedApprovers,
            );
          } else {
            this.approvalConfigDetails = [];
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalConfigDetails = [];
          this.approvalSteps = [];
        });
    },
    fetchDetail(id) {
      ApiService.get(`v2/purchase-requests/${id}`)
        .then(({ data }) => {
          const pr = data.data;
          const period = pr.period ? new Date(pr.period) : new Date();
          this.form = {
            id: pr.id,
            companyId: pr.companyId,
            departmentId: pr.departmentId,
            costItemId: pr.costItemId,
            storeId: pr.storeId,
            month: period.getMonth() + 1,
            year: period.getFullYear(),
            requestedAmount: pr.requestedAmount,
            budgetUsagePeriod: pr.budgetUsagePeriod || 1,
            description: pr.description,
          };
          this.yearModel = moment(String(this.form.year), 'YYYY');
          this.syncDepartmentModel();
          this.syncStoreModel();
          this.fetchApprovalConfig(pr.approvers || []);
          this.checkBudget();
        })
        .catch((err) => handleError(err));
    },
    onBudgetParamChange() {
      this.debouncedCheckBudget();
    },
    checkBudget() {
      const {
        departmentId,
        costItemId,
        month,
        year,
        requestedAmount,
        budgetUsagePeriod,
        storeId,
      } = this.form;
      if (!departmentId || !costItemId || !month || !year) return;

      const params = {
        departmentId,
        costItemId,
        month,
        year,
        companyId: this.form.companyId,
        requestedAmount: requestedAmount || 0,
        budgetUsagePeriod: budgetUsagePeriod || 1,
      };
      if (storeId) params.storeId = storeId;

      ApiService.query('v2/purchase-requests/budget-check', { params })
        .then(({ data }) => {
          this.budgetCheck = data.data;
        })
        .catch((err) => handleError(err));
    },
    onSubmit() {
      if (!this.form.companyId) {
        this.makeToastFaile('Vui lòng chọn doanh nghiệp!');
        return;
      }
      if (
        !this.form.departmentId ||
        !this.form.costItemId ||
        !this.form.storeId ||
        !this.form.requestedAmount
      ) {
        this.makeToastFaile('Vui lòng điền đầy đủ thông tin bắt buộc!');
        return;
      }
      if (!this.approvalSteps.length) {
        this.makeToastFaile('Chưa có cấu hình quy trình duyệt!');
        return;
      }
      const invalidStep = this.approvalSteps.find((step) => !step.approverBy);
      if (invalidStep) {
        this.makeToastFaile(
          `Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`,
        );
        return;
      }
      this.isSaving = true;
      const payload = {
        ...this.form,
        approvalType: this.approvalType,
        approvers: this.approvalSteps
          .map((step) => ({
            step: step.step,
            approverBy: step.approverBy,
          }))
          .sort((a, b) => a.step - b.step),
      };
      const request = this.isEdit
        ? ApiService.put('v2/purchase-requests', payload)
        : ApiService.post('v2/purchase-requests', payload);

      request
        .then(() => {
          this.makeToastSuccess(
            this.isEdit
              ? 'Cập nhật phiếu đề nghị mua hàng thành công!'
              : 'Tạo phiếu đề nghị mua hàng thành công!',
          );
          this.$router.push({ name: 'list-purchase-requests' });
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      this.$router.push({ name: 'list-purchase-requests' });
    },
  },
};
</script>

<style scoped>
.pr-year-picker {
  cursor: pointer;
}
</style>
